import { createModule } from '../../createModule';
export const createICReportAPI = createModule(({ http }) => {
    /**
     * Get any data in the IC Report.
     *
     * The IC Report Data API uses a single endpoint, but swaps out the path
     * in order to dictate what data gets returned.
     */
    const getData = async ({ endpoint, fund_name, st_dt, ed_dt, force_month_end, }) => {
        const res = await http.get(`/v2/ic_report/method/${endpoint}`, {
            params: {
                fund_name,
                st_dt,
                ed_dt: ed_dt ?? st_dt,
                force_month_end,
            },
        });
        return res;
    };
    /**
     * Get the IC Report Template for a given fund across a date range
     */
    const getTemplate = async (params) => {
        const res = await http.get(`/v2/ic_report/template`, {
            params,
        });
        return res;
    };
    /**
     * Helper to type return data and abstract params.
     *
     * Basically just helps to prevent this file from getting really bulky.
     *
     */
    const bindEndpoint = (endpoint) => {
        return (params) => {
            return getData({ endpoint, ...params });
        };
    };
    return {
        getTemplate,
        fund_level: {
            getFundLevel: bindEndpoint('fund_level'),
            getFundLevelMetrics: bindEndpoint('fund_level_metrics'),
            getRebasedNav: bindEndpoint('rebased_nav'),
            getBucketedAttributions: bindEndpoint('attributions_bucketed'),
            getTrackingDifferences: bindEndpoint('tracking_differences'),
            getLiquidityReporting: bindEndpoint('liquidity_reporting'),
            getStressTest: bindEndpoint('bear_stress_test'),
            getHistoricalEvents: bindEndpoint('historical_events'),
            getHistoricalEventsNew: bindEndpoint('scenario_analysis_fund_level'),
        },
        beta: {
            getDetailedBreakdown: bindEndpoint('detailed_breakdown'),
            getAggregatedBreakdown: bindEndpoint('aggregated_breakdown'),
            getOptions: bindEndpoint('beta_options'),
            getNewData: bindEndpoint('new_data'),
            getOldData: bindEndpoint('old_data'),
            getCapitalGrowth: bindEndpoint('capital_growth'),
            getCapitalGrowthBarriers: bindEndpoint('capital_growth_barriers'),
            getCapitalProtection: bindEndpoint('capital_protection'),
            getCapitalProtectionBarriers: bindEndpoint('capital_protection_barriers'),
            getHistoricalEvents: bindEndpoint('historical_events_beta'),
            getHistoricalEventsNew: bindEndpoint('scenario_analysis_beta'),
        },
        income: {
            getDuration: bindEndpoint('income_duration'),
            getRatingChange: bindEndpoint('rating_change'),
            getEodCash: bindEndpoint('eod_cash_balances'),
            getBucketVsOvernightDifferences: bindEndpoint('bucket_vs_overnight'),
            getLockedYield: bindEndpoint('locked_yield'),
            getPerIssuer: bindEndpoint('per_issuer'),
        },
        diversifiers: {
            getBucketedAttributions: bindEndpoint('attributions_bucketed_diversifiers'),
            getRebased100Monthly: bindEndpoint('perf_rebased_100'),
            getRebased0Monthly: bindEndpoint('monthly_perf_rebased_0'),
            getRebased0Yearly: bindEndpoint('yearly_perf_rebased_0'),
            getContribution: bindEndpoint('perf_contribution'),
            getMetrics: bindEndpoint('metrics'),
            getFx: bindEndpoint('fx_diversifiers'),
            getRatesVolatilities: bindEndpoint('rates_volatilities'),
            getHistoricalEvents: bindEndpoint('historical_events_diversifiers'),
            getHistoricalEventsNew: bindEndpoint('scenario_analysis_diversifiers'),
            getCtaExposure: bindEndpoint('cta_exposure'),
        },
        commodities: {
            getHistoricalEventsBcom: bindEndpoint('historical_events_bcom'),
            getCurves: bindEndpoint('commodity_curves'),
            getBackwardation: bindEndpoint('commodities_backwardation'),
            getBearBull: bindEndpoint('bcom_bear_bull_chart'),
        },
        protection: {
            getOptions: bindEndpoint('protection_options'),
            getBreakdown: bindEndpoint('protection_breakdown'),
            getHistoricalEvents: bindEndpoint('protection_scenario'),
            getHistoricalEventsNew: bindEndpoint('scenario_analysis_protection'),
        },
    };
});
